import { db } from '../firebase/config';
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { getUserPublicData } from './userService';
import { UserPublicData } from '../types/user';

export interface SuggestedAthlete {
  userId: string;
  addedAt: Date;
  addedBy: string;
  order: number;
}

export interface SuggestedAthleteWithData extends SuggestedAthlete {
  publicData: UserPublicData | null;
}

/**
 * Add a user to suggested athletes (admin only)
 */
export const addSuggestedAthlete = async (
  userId: string,
  addedBy: string
): Promise<void> => {
  try {
    // Get current count to determine order
    const suggestedAthletesRef = collection(db, 'suggestedAthletes');
    const snapshot = await getDocs(suggestedAthletesRef);
    const order = snapshot.size;

    const athleteRef = doc(db, 'suggestedAthletes', userId);
    await setDoc(athleteRef, {
      userId,
      addedAt: Timestamp.now(),
      addedBy,
      order
    });
  } catch (error) {
    console.error('Error adding suggested athlete:', error);
    throw error;
  }
};

/**
 * Remove a user from suggested athletes (admin only)
 */
export const removeSuggestedAthlete = async (userId: string): Promise<void> => {
  try {
    const athleteRef = doc(db, 'suggestedAthletes', userId);
    await deleteDoc(athleteRef);
  } catch (error) {
    console.error('Error removing suggested athlete:', error);
    throw error;
  }
};

/**
 * Get all suggested athletes
 */
export const getSuggestedAthletes = async (): Promise<SuggestedAthleteWithData[]> => {
  try {
    const suggestedAthletesRef = collection(db, 'suggestedAthletes');
    const q = query(suggestedAthletesRef, orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    
    const athletes: SuggestedAthleteWithData[] = await Promise.all(
      snapshot.docs.map(async (docSnapshot) => {
        const data = docSnapshot.data();
        const publicData = await getUserPublicData(data.userId);
        
        return {
          userId: data.userId,
          addedAt: data.addedAt?.toDate() || new Date(),
          addedBy: data.addedBy || '',
          order: data.order || 0,
          publicData
        };
      })
    );
    
    return athletes;
  } catch (error) {
    console.error('Error getting suggested athletes:', error);
    throw error;
  }
};

/**
 * Check if a user is a suggested athlete
 */
export const isSuggestedAthlete = async (userId: string): Promise<boolean> => {
  try {
    const athleteRef = doc(db, 'suggestedAthletes', userId);
    const snapshot = await getDoc(athleteRef);
    return snapshot.exists();
  } catch (error) {
    console.error('Error checking suggested athlete:', error);
    return false;
  }
};

/**
 * Update the order of suggested athletes (admin only)
 */
export const updateSuggestedAthletesOrder = async (
  userIds: string[]
): Promise<void> => {
  try {
    const updates = userIds.map((userId, index) => {
      const athleteRef = doc(db, 'suggestedAthletes', userId);
      return setDoc(athleteRef, { order: index }, { merge: true });
    });
    
    await Promise.all(updates);
  } catch (error) {
    console.error('Error updating suggested athletes order:', error);
    throw error;
  }
};

